import { SectionHeading } from "@/components/shared/SectionHeading";
import { Reveal, RevealStagger, RevealItem } from "@/components/shared/Reveal";
import { ArrowRight, CheckCircle2 } from "lucide-react";
import { openPreRegister } from "@/lib/preRegisterModal";

const OPPORTUNITIES = [
  "Temporary truck driving", "Relief driving", "Relay assignments", "Owner operator replacement driving",
  "Carrier assignments", "RV recovery", "RV delivery", "Dealer transfers", "Fleet repositioning",
];

const VERIFICATION = [
  "Identity", "CDL", "CDL class", "Endorsements", "Restrictions", "Medical qualification",
  "MVR", "Background screening", "Drug & alcohol compliance", "Training", "Equipment experience",
  "Insurance eligibility", "ELD/HOS information",
];

export const Drivers = () => {
  return (
    <section id="drivers" data-testid="drivers-section" className="py-24 md:py-32 bg-white overflow-hidden">
      <div className="max-w-7xl mx-auto px-5 md:px-8 grid lg:grid-cols-[0.9fr_1.1fr] gap-16 items-start">
        <div>
          <SectionHeading
            eyebrow="For Drivers"
            title="Your Experience Can Earn, Even If the Truck Isn't Yours."
            description="RelayRigs gives qualified drivers access to transportation opportunities without requiring every driver to own commercial equipment. Build a professional RelayRigs profile containing your qualifications and experience."
            testId="drivers"
          />
          <Reveal delay={0.25}>
            <div className="mt-8 flex flex-wrap gap-2">
              {OPPORTUNITIES.map((o) => (
                <span key={o} data-testid={`driver-opportunity-${o.toLowerCase().replace(/\s+/g, "-")}`} className="text-xs font-semibold bg-rr-surface border border-rr-line text-rr-navy px-3 py-1.5 rounded-full">
                  {o}
                </span>
              ))}
            </div>
          </Reveal>
          <Reveal delay={0.35}>
            <button onClick={() => openPreRegister("driver")} data-testid="become-driver-cta" className="mt-9 inline-flex items-center gap-2 bg-rr-orange text-white font-bold px-7 py-4 rounded-full hover:bg-rr-navy transition-all hover:-translate-y-1">
              Pre-Register as a Driver <ArrowRight size={18} />
            </button>
          </Reveal>
        </div>

        <Reveal delay={0.2}>
          <div className="bg-rr-navy rounded-[2rem] p-8 text-white shadow-2xl">
            <div className="flex items-center justify-between mb-6">
              <span className="text-xs font-bold tracking-widest uppercase text-rr-orangeLight">Assignment Preview</span>
              <span className="text-xs font-bold bg-white/10 px-2.5 py-1 rounded-full">Match Found</span>
            </div>
            <h4 className="font-heading text-2xl font-bold">Jacksonville, FL → Charleston, SC</h4>
            <div className="grid grid-cols-3 gap-3 mt-5 text-sm">
              <div><div className="text-white/40 text-xs">Mileage</div><div className="font-bold">346 mi</div></div>
              <div><div className="text-white/40 text-xs">Equipment</div><div className="font-bold">Dry Van</div></div>
              <div><div className="text-white/40 text-xs">Compensation</div><div className="font-bold text-rr-orangeLight">$1,850</div></div>
              <div><div className="text-white/40 text-xs">Drive Time</div><div className="font-bold">~6h 10m</div></div>
              <div><div className="text-white/40 text-xs">HOS</div><div className="font-bold text-rr-green">Compatible</div></div>
              <div><div className="text-white/40 text-xs">Payment</div><div className="font-bold text-rr-green">Secured</div></div>
            </div>
            <div className="mt-6 pt-5 border-t border-white/10 text-xs text-white/50">Required: CDL-A, tanker endorsement not required</div>
          </div>
        </Reveal>
      </div>

      <div className="max-w-7xl mx-auto px-5 md:px-8 mt-24">
        <SectionHeading eyebrow="Driver Verification" title="Qualifications Matter." testId="driver-verification" />
        <RevealStagger className="mt-10 grid sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3">
          {VERIFICATION.map((v) => (
            <RevealItem key={v}>
              <div data-testid={`verification-item-${v.toLowerCase().replace(/[^a-z]+/g, "-")}`} className="flex items-center gap-2 text-sm font-semibold text-rr-navy bg-rr-surface rounded-lg px-4 py-3">
                <CheckCircle2 size={16} className="text-rr-green shrink-0" /> {v}
              </div>
            </RevealItem>
          ))}
        </RevealStagger>
        <p className="text-xs text-rr-muted mt-6 max-w-2xl">
          RelayRigs verification workflows are designed to build trust into the marketplace and do not represent government certification.
        </p>
      </div>
    </section>
  );
};
